import React, { useState, useEffect } from 'react';
import {
    Container,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Checkbox,
    Button,
    Typography,
    TableContainer,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Paper,
    Fade,
    LinearProgress,
    Stack,
    Box,
    Switch,
    TextField,
    FormControlLabel,
    Alert,
    useMediaQuery,
    useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuthContext } from '../context/AuthContext';

import { HEADER_FONT } from '../styles/theme';
import { Track, Series, ReturnData, Artist, Content, Settings } from '../models/TrackInstallVisibility';
import { trackBytes, seriesBytes, selectedContentBytes, formatBytes, headsetStorageIssues } from '../utils/storage';

const deepCopy = <T,>(obj: T): T => {
    return JSON.parse(JSON.stringify(obj));
    // return JSON.parse(JSON.stringify(obj)).content;
};

// Used when the institution has never saved any settings: the menu is off by default
const defaultSettings: Settings = {
    settingsAvailable: false,
    disableDate: '',
};

// Layout constants that keep every Download checkbox, and every Visible checkbox,
// in one vertical line across the "All content" row, the artist rows and the
// track rows inside each artist.
const CONTROL_WIDTH = 190;       // width of one checkbox + label in the header rows
const SUMMARY_RIGHT_INSET = 40;  // artist row: 16px padding + 24px expand icon
const TABLE_RIGHT_INSET = 16;    // track table: padding of the expanded panel
const CELL_PADDING = 16;         // left padding inside a table cell
// Track and series titles use the header font
const titleSx = { fontFamily: HEADER_FONT, fontSize: '1.35rem', letterSpacing: '0.03em', lineHeight: 1.2, color: '#d9d9d6' };
// Artist names: same font, larger and full white
const artistSx = { fontFamily: HEADER_FONT, fontSize: '1.9rem', fontWeight: 400, letterSpacing: '0.03em', lineHeight: 1.1 };
const controlSx = { display: 'flex', alignItems: 'center', width: `${CONTROL_WIDTH}px`, flexShrink: 0 };

type Field = 'install' | 'visibility';
type ItemKind = 'track' | 'series';

// Everything that can be provisioned for an artist: standalone tracks, and each
// series as a single item
const artistItems = (artist: Artist): (Track | Series)[] => {
    return [...(artist.tracks ?? []), ...(artist.series ?? [])];
};

const initializeData = (data: Content): Content => {
    const newData = deepCopy(data);
    newData.settings = { ...defaultSettings, ...(newData.settings ?? {}) };
    // Tolerate a catalogue with missing lists so rendering never fails
    if (!Array.isArray(newData.artists)) newData.artists = [];
    newData.artists.forEach((artist) => {
        if (!Array.isArray(artist.tracks)) artist.tracks = [];
        if (artist.series !== undefined && !Array.isArray(artist.series)) artist.series = [];
    });
    console.log(typeof (newData));
    // newData.artists.forEach((artist) => {
    //     if (Array.isArray(artist.tracks)) {
    //         artist.tracks.forEach((track) => {
    //             if (track.visibility === undefined) track.visibility = false; // default visibility to false
    //             if (track.install === undefined) track.install = false; // default install to false
    //         });
    //     } else {
    //         artist.tracks = []; // Initialize tracks as an empty array if it's undefined
    //     }
    // });
    return newData;
};

const TrackTable: React.FC = () => {

    // State management for track data:
    // - soapboxData: Raw data from the API
    // - data: Processed and initialized data for rendering and local changes
    // - originalData: A copy of the initial data for comparison
    // - newData: Temporary copy used for updates before setting to 'data'
    //
    // This structure allows for local changes, comparison with original data,
    // and efficient updates without directly mutating the state.
    const { soapboxData, institution, setTrackInstallAndVisibility, superUser } = useAuthContext(); 
    const [data, setData] = useState<Content | null>(null);
    const [originalData, setOriginalData] = useState<Content | null>(null);
    const [changesMadeLocally, setChangesMadeLocally] = useState<boolean>(false);
    const [changesInPendingCommit, setChangesInPendingCommit] = useState<boolean>(false);

    const theme: any = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (soapboxData) {
            const initializedData = initializeData(soapboxData);
            setData(initializedData);
            setOriginalData(initializedData);
            // console.log('Initialized data:', initializedData);
            setChangesInPendingCommit(false);
            // console.log("soapbox data updated")
        }
    }, [soapboxData]);

    useEffect(() => {
        // console.log("unsaved changes ", changesInPendingCommit)
    }, [changesInPendingCommit])

    const handleCheckboxChange = (artistId: string, itemId: string, field: Field, kind: ItemKind = 'track') => {
        if (!data) return;
        const newData = deepCopy(data);
        const artist = newData.artists.find((artist) => artist.id === artistId);
        if (artist) {
            const items: (Track | Series)[] = kind === 'series' ? (artist.series ?? []) : artist.tracks;
            const item = items.find((item) => item.id === itemId);
            if (item) {
                item[field] = !item[field];
                if (field === 'install' && !item.install) {
                    item.visibility = false;
                }
                setData(newData);
                setChangesMadeLocally(true);
            }
        }
    };

    const handleArtistCheckboxChange = (artistId: string, checked: boolean, field: Field) => {
        if (!data) return;
        const newData = deepCopy(data);
        const artist = newData.artists.find((artist) => artist.id === artistId);
        if (artist) {
            artistItems(artist).forEach((item) => {
                item[field] = checked;
                if (field === 'install' && !checked) {
                    item.visibility = false;
                }
            });
            setData(newData);
            setChangesMadeLocally(true);
        }
    };

    const handleSettingChange = (field: keyof Settings, checked: boolean | string) => {
        if (!data) return;
        const newData = deepCopy(data);
        newData.settings = { ...defaultSettings, ...(newData.settings ?? {}), [field]: checked };
        setData(newData);
        setChangesMadeLocally(true);
    };

    // Select all / none across every artist
    const handleAllCheckboxChange = (checked: boolean, field: Field) => {
        if (!data) return;
        const newData = deepCopy(data);
        newData.artists.forEach((artist) => {
            artistItems(artist).forEach((item) => {
                item[field] = checked;
                if (field === 'install' && !checked) {
                    item.visibility = false;
                }
            });
        });
        setData(newData);
        setChangesMadeLocally(true);
    };

    const handleRevert = () => {
        if (originalData) {
            setData(deepCopy(originalData));
            setChangesMadeLocally(false);
        }
    };

    const handleSave = () => {
        if (data) {
            // A selection that doesn't fit on every headset is never saved (the
            // Save button is disabled as well)
            if (headsetStorageIssues(data, institution?.devices).length > 0) return;
            const strippedData = stripData(data);
            setOriginalData(deepCopy(data));
            setChangesMadeLocally(false);
            setChangesInPendingCommit(true);
            saveContent(strippedData);
        }
    };

    const stripData = (data: Content): ReturnData => {
        return {
            artists: data.artists.map((artist) => ({
                id: artist.id,
                tracks: artist.tracks.map((track) => ({
                    id: track.id,
                    visibility: track.visibility,
                    install: track.install,
                })),
                // a series only stores install and visibility at the series level
                series: (artist.series ?? []).map((series) => ({
                    id: series.id,
                    visibility: !!series.visibility,
                    install: !!series.install,
                })),
            })),
            settings: { ...defaultSettings, ...(data.settings ?? {}) },
        };
    };

    const saveContent = (data: ReturnData) => {
        setTrackInstallAndVisibility(data);
    };

    const getArtistCheckboxState = (artist: Artist) => {
        const allDownloaded = artistItems(artist).every((item) => item.install);
        const noneDownloaded = artistItems(artist).every((item) => !item.install);
        if (allDownloaded) return true;
        if (noneDownloaded) return false;
        return 'indeterminate';
    };

    const getArtistVisibilityCheckboxState = (artist: Artist) => {
        const allVisible = artistItems(artist).every((item) => item.visibility);
        const noneVisible = artistItems(artist).every((item) => !item.visibility);
        if (allVisible) return true;
        if (noneVisible) return false;
        return 'indeterminate';
    };

    if (!data) {
        return <div>Loading...</div>;
    }

    // Total size of what is currently ticked for download (including unsaved
    // changes), compared with the total storage each headset last reported
    const selectedBytes = selectedContentBytes(data);
    // State of the "all" checkboxes: true, false, or 'indeterminate' when mixed
    const allItems = data.artists.flatMap((artist) => artistItems(artist));
    const getAllCheckboxState = (field: Field) => {
        if (allItems.length === 0) return false;
        if (allItems.every((item) => item[field])) return true;
        if (allItems.every((item) => !item[field])) return false;
        return 'indeterminate';
    };
    const allInstallState = getAllCheckboxState('install');
    const allVisibilityState = getAllCheckboxState('visibility');

    // Headsets the current selection would not fit on (see headsetStorageIssues)
    const storageIssues = headsetStorageIssues(data, institution?.devices);

    // Whether the saved-or-edited licence end date is today or earlier (local date)
    const todayLocal = (() => {
        const d = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    })();
    // "2026-11-01" -> "1 November 2026"
    const formatLicenceDate = (value: string) => {
        const [y, m, d] = value.split('-').map(Number);
        if (!y || !m || !d) return value;
        return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    };
    const licenceDateEnabled = !!data?.settings?.disableDate;
    // Starting point when the end date is switched on: one year from today
    const defaultLicenceEndDate = () => {
        const d = new Date();
        d.setFullYear(d.getFullYear() + 1);
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };
    const licenceEnded = !!data?.settings?.disableDate && data.settings.disableDate <= todayLocal;

    return (
        <Container component="main" maxWidth="lg" sx={{ mt: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: 'auto' }}>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!changesMadeLocally || changesInPendingCommit || storageIssues.length > 0}
                    onClick={handleSave}
                >
                    Save my changes
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    disabled={!changesMadeLocally || changesInPendingCommit}
                    onClick={handleRevert}
                >
                    Revert unsaved changes
                </Button>
            </Stack>
            <Fade in={changesInPendingCommit} unmountOnExit>
                <LinearProgress color="primary" sx={{ mt: 1, mb: 1, height: '5px', width: '100%' }} />
            </Fade>
            <Paper sx={{ mt: 2, p: 2 }}>
                {/* Settings available: everyone sees it, only Soapbox staff can change it (the server ignores changes from anyone else) */}
                {!superUser && (
                    <Box>
                        <Typography variant="body1">
                            Settings menu on headsets: <strong>{(data.settings?.settingsAvailable ?? defaultSettings.settingsAvailable) ? 'Available' : 'Not available'}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            The settings menu in the Soapbox app includes the option to switch on streaming mode,
                            which plays content without downloading it in full first. To change this, contact support@soapbox.us.
                        </Typography>
                    </Box>
                )}
                {superUser && (<>
                <FormControlLabel
                    control={
                        <Switch
                            checked={data.settings?.settingsAvailable ?? defaultSettings.settingsAvailable}
                            onChange={(e) => handleSettingChange('settingsAvailable', e.target.checked)}
                            color="primary"
                            disabled={changesInPendingCommit}
                        />
                    }
                    label="Settings available"
                />
                <Typography variant="body2" color="text.secondary">
                    Turn on to make the settings menu available in the Soapbox app on this institution's headsets.
                    This includes the option to switch on streaming mode, which plays content without downloading it in full first.
                    The institution can see this but only Soapbox staff can change it.
                </Typography>
                </>)}
                {/* Licence end date: everyone sees it, only Soapbox staff can change it (the server ignores changes from anyone else) */}
                {!superUser && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="body1">
                            Licence end date: <strong>{licenceDateEnabled ? formatLicenceDate(data.settings?.disableDate ?? '') : 'No end date'}</strong>
                        </Typography>
                        {licenceEnded && (
                            <Typography variant="body2" color="error">
                                This date has passed: headsets are showing the contact support message.
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {licenceDateEnabled
                                ? 'From this date the Soapbox app stops showing content on your headsets, online or offline. '
                                : ''}
                            To change your licence, contact support@soapbox.us.
                        </Typography>
                    </Box>
                )}
                {superUser && (<>
                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={licenceDateEnabled}
                                onChange={(e) => handleSettingChange('disableDate', e.target.checked ? defaultLicenceEndDate() : '')}
                                color="primary"
                                disabled={changesInPendingCommit}
                            />
                        }
                        label="Licence end date"
                    />
                    {licenceDateEnabled ? (
                        <TextField
                            label="End date"
                            type="date"
                            size="small"
                            value={data.settings?.disableDate ?? ''}
                            // clearing the date in the picker is ignored: use the switch to turn it off
                            onChange={(e) => { if (e.target.value) handleSettingChange('disableDate', e.target.value); }}
                            InputLabelProps={{ shrink: true }}
                            disabled={changesInPendingCommit}
                            sx={{ width: 220 }}
                        />
                    ) : (
                        <TextField
                            label="End date"
                            size="small"
                            value="Disabled"
                            InputLabelProps={{ shrink: true }}
                            disabled
                            sx={{ width: 220 }}
                        />
                    )}
                    {licenceEnded && (
                        <Typography variant="body2" color="error">
                            This date has passed: headsets are showing the contact support message.
                        </Typography>
                    )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    When turned on, the Soapbox app stops showing content on this institution's headsets from this date, online
                    or offline, and asks the user to contact support@soapbox.us. A headset must connect to the
                    server after the date is set or changed for it to take effect on that headset. When turned
                    off there is no end date. The institution can see this date but only Soapbox staff can change it.
                </Typography>
                </>)}
            </Paper>
            <Paper sx={{ mt: 2, p: 2 }}>
                <Typography variant="body1">
                    Selected for download: <strong>{formatBytes(selectedBytes)}</strong>
                </Typography>
                {storageIssues.length > 0 && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        This selection does not fit on {storageIssues.length === 1 ? 'this headset' : 'these headsets'} so it can't be saved:{' '}
                        {storageIssues.map((issue) =>
                            `${issue.name} (needs ${formatBytes(issue.neededBytes)}, has room for ${formatBytes(issue.availableBytes)})`).join(', ')}.
                        Untick some downloads to save.
                    </Alert>
                )}
            </Paper>
            <TableContainer component={Paper} style={{ marginTop: '20px' }}>
                <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, pr: isMobile ? 2 : `${SUMMARY_RIGHT_INSET}px` }}>
                    <Typography variant="h6">All content</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={controlSx}>
                        <Checkbox
                            checked={allInstallState === true}
                            indeterminate={allInstallState === 'indeterminate'}
                            onChange={(e) => handleAllCheckboxChange(e.target.checked, 'install')}
                            disabled={allItems.length === 0 || changesInPendingCommit}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Download all {allItems.filter((item) => item.install).length}/{allItems.length}</Typography>
                        </Box>
                        <Box sx={controlSx}>
                        <Checkbox
                            checked={allVisibilityState === true}
                            indeterminate={allVisibilityState === 'indeterminate'}
                            onChange={(e) => handleAllCheckboxChange(e.target.checked, 'visibility')}
                            disabled={allItems.length === 0 || changesInPendingCommit || allItems.some((item) => !item.install)}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Visible all {allItems.filter((item) => item.visibility).length}/{allItems.length}</Typography>
                        </Box>
                    </Box>
                </Box>
                {data.artists.filter((artist) => artistItems(artist).length > 0).map((artist) => (
                    <Accordion key={artist.id} defaultExpanded={false}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel-${artist.id}-content`}
                            id={`panel-${artist.id}-header`}
                        >
                            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                <Typography variant="h5" sx={artistSx}>{artist.name}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: isMobile ? 1 : 0 }}>
                                    <Box sx={controlSx}>
                                    <Checkbox
                                        checked={getArtistCheckboxState(artist) === true}
                                        indeterminate={getArtistCheckboxState(artist) === 'indeterminate'}
                                        onChange={(e) => handleArtistCheckboxChange(artist.id, e.target.checked, 'install')}
                                        onClick={(e) => e.stopPropagation()} // Prevent accordion toggle
                                    />
                                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Download {artistItems(artist).filter((item) => item.install).length}/{artistItems(artist).length}</Typography>
                                    </Box>
                                    <Box sx={controlSx}>
                                    <Checkbox
                                        checked={getArtistVisibilityCheckboxState(artist) === true}
                                        indeterminate={getArtistVisibilityCheckboxState(artist) === 'indeterminate'}
                                        onChange={(e) => handleArtistCheckboxChange(artist.id, e.target.checked, 'visibility')}
                                        onClick={(e) => e.stopPropagation()} // Prevent accordion toggle
                                        disabled={artistItems(artist).some((item) => !item.install)}
                                    />
                                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Visible {artistItems(artist).filter((item) => item.visibility).length}/{artistItems(artist).length}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Table sx={{ tableLayout: 'fixed' }}>
                                <TableHead>
                                    <TableRow key="header-row">
                                        <TableCell key="header-track-name">Track Name</TableCell>
                                        <TableCell key="header-size" sx={{ width: '120px' }}>Size</TableCell>
                                        <TableCell key="header-install" sx={{ width: `${CONTROL_WIDTH}px` }}>Download</TableCell>
                                        <TableCell key="header-visibility" sx={{ width: `${CONTROL_WIDTH + SUMMARY_RIGHT_INSET - TABLE_RIGHT_INSET + CELL_PADDING}px` }}>Visible</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {artist.tracks.map((track) => (
                                        <TableRow key={`track-${track.id}`}>
                                            <TableCell sx={titleSx}>{track.title}</TableCell>
                                            <TableCell sx={{ width: '120px' }}>{formatBytes(trackBytes(track))}</TableCell>
                                            <TableCell>
                                                <Checkbox
                                                    checked={track.install}
                                                    onChange={() =>
                                                        handleCheckboxChange(artist.id, track.id, 'install')
                                                    }
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Checkbox
                                                    checked={track.visibility}
                                                    onChange={() =>
                                                        handleCheckboxChange(artist.id, track.id, 'visibility')
                                                    }
                                                    color="primary"
                                                    disabled={!track.install}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {(artist.series ?? []).map((series) => (
                                        <TableRow key={`series-${series.id}`}>
                                            <TableCell>
                                                <Typography component="span" sx={{ ...titleSx, display: 'block' }}>{series.title}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Series{series.tracks ? ` \u00b7 ${series.tracks.length} tracks` : ''}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ width: '120px' }}>{formatBytes(seriesBytes(series))}</TableCell>
                                            <TableCell>
                                                <Checkbox
                                                    checked={!!series.install}
                                                    onChange={() =>
                                                        handleCheckboxChange(artist.id, series.id, 'install', 'series')
                                                    }
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Checkbox
                                                    checked={!!series.visibility}
                                                    onChange={() =>
                                                        handleCheckboxChange(artist.id, series.id, 'visibility', 'series')
                                                    }
                                                    color="primary"
                                                    disabled={!series.install}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </TableContainer>
        </Container >
    );
};

export default TrackTable;